import type {
  DiscoveryEvent,
  ProfileSummary,
  VenuePublicDeal,
  VenueSummary,
} from '@/lib/api/apiTypes';

export type SearchIntent = 'balanced' | 'deals' | 'events' | 'people' | 'scene';

export type SmartSearchResult =
  | {
      key: string;
      type: 'venue';
      item: VenueSummary;
      reason: string;
    }
  | {
      key: string;
      type: 'deal';
      item: VenuePublicDeal;
      reason: string;
    }
  | {
      key: string;
      type: 'event';
      item: DiscoveryEvent;
      moreCount?: number;
      reason: string;
    }
  | {
      key: string;
      type: 'profile';
      item: ProfileSummary;
      reason: string;
    };

type ScoredSmartSearchResult = SmartSearchResult & {
  duplicateGroupKey?: string;
  score: number;
};

const DEAL_TERMS = [
  'happy hour',
  'deal',
  'deals',
  'drink',
  'drinks',
  'food',
  'special',
  'specials',
];
const EVENT_TERMS = [
  'tonight',
  'tomorrow',
  'weekend',
  'live',
  'live music',
  'music',
  'dj',
  'trivia',
  'karaoke',
  'comedy',
  'concert',
  'show',
  'party',
  'free',
];
const SCENE_TERMS = [
  'art',
  'brunch',
  'country',
  'drag',
  'edm',
  'house',
  'jazz',
  'latin',
  'market',
  'open mic',
  'pop',
  'punk',
  'r&b',
  'rap',
  'rock',
  'salsa',
  'techno',
];

function normalizeSearchText(value: string | null | undefined) {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

function includesAny(query: string, terms: string[]) {
  return terms.some((term) => query.includes(term));
}

function normalizeGroupText(value: string | null | undefined) {
  return normalizeSearchText(value)
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function textMatchScore(query: string, value: string | null | undefined) {
  const text = normalizeSearchText(value);
  if (!text || !query) {
    return 0;
  }

  if (text === query) {
    return 90;
  }

  if (text.startsWith(query)) {
    return 68;
  }

  if (text.includes(query)) {
    return 46;
  }

  return 0;
}

export function detectSearchIntent(query: string): SearchIntent {
  const normalizedQuery = normalizeSearchText(query);

  // @ is an explicit people-search hint, so it outranks keyword matches like event names or venues.
  if (normalizedQuery.startsWith('@')) {
    return 'people';
  }

  if (includesAny(normalizedQuery, DEAL_TERMS)) {
    return 'deals';
  }

  if (includesAny(normalizedQuery, EVENT_TERMS)) {
    return 'events';
  }

  if (includesAny(normalizedQuery, SCENE_TERMS)) {
    return 'scene';
  }

  return 'balanced';
}

function intentPriority(intent: SearchIntent) {
  switch (intent) {
    case 'deals':
      return ['deal', 'venue', 'event', 'profile'];
    case 'events':
      return ['event', 'venue', 'deal', 'profile'];
    case 'people':
      return ['profile', 'venue', 'event', 'deal'];
    case 'scene':
      return ['venue', 'event', 'deal', 'profile'];
    case 'balanced':
      return ['venue', 'deal', 'event', 'profile'];
  }
}

function intentBoost(intent: SearchIntent, type: SmartSearchResult['type']) {
  if (intent === 'deals' && type === 'deal') {
    return 38;
  }

  if (intent === 'events' && type === 'event') {
    return 34;
  }

  if (intent === 'people' && type === 'profile') {
    return 40;
  }

  if (intent === 'scene' && type === 'venue') {
    return 36;
  }

  if (intent === 'scene' && type === 'event') {
    return 24;
  }

  if (intent === 'scene' && type === 'deal') {
    return 12;
  }

  if (intent === 'balanced' && type === 'venue') {
    return 18;
  }

  if (intent === 'balanced' && type === 'deal') {
    return 10;
  }

  return 0;
}

function resultReason(intent: SearchIntent, type: SmartSearchResult['type'], exactMatch: boolean) {
  if (exactMatch) {
    return 'Best match';
  }

  if (intent === 'deals' && type === 'deal') {
    return 'Deal match';
  }

  if (intent === 'events' && type === 'event') {
    return 'Event match';
  }

  if (intent === 'people' && type === 'profile') {
    return 'Profile match';
  }

  if (intent === 'scene' && type === 'venue') {
    return 'Scene match';
  }

  if (intent === 'scene' && type === 'event') {
    return 'Next event';
  }

  if (intent === 'scene' && type === 'deal') {
    return 'Nearby deal';
  }

  switch (type) {
    case 'venue':
      return 'Venue match';
    case 'deal':
      return 'Deal match';
    case 'event':
      return 'Event match';
    case 'profile':
      return 'Profile match';
  }
}

function sortSmartResults(results: ScoredSmartSearchResult[], intent: SearchIntent) {
  const priority = intentPriority(intent);

  return [...results].sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }

    return priority.indexOf(a.type) - priority.indexOf(b.type);
  });
}

function getEventDuplicateGroupKey(event: DiscoveryEvent) {
  const venueKey = event.venue_id || normalizeGroupText(event.venue_name);
  const titleKey = normalizeGroupText(event.title);

  if (!venueKey || !titleKey) {
    return null;
  }

  return `${venueKey}:${titleKey}`;
}

function collapseDuplicateEvents(results: ScoredSmartSearchResult[]) {
  const primaryByGroup = new Map<string, ScoredSmartSearchResult>();
  const collapsed: ScoredSmartSearchResult[] = [];

  results.forEach((result) => {
    if (result.type !== 'event' || !result.duplicateGroupKey) {
      collapsed.push(result);
      return;
    }

    const primary = primaryByGroup.get(result.duplicateGroupKey);

    if (!primary) {
      primaryByGroup.set(result.duplicateGroupKey, result);
      collapsed.push(result);
      return;
    }

    if (primary.type === 'event') {
      primary.moreCount = (primary.moreCount ?? 0) + 1;
    }
  });

  return collapsed;
}

export function buildSmartSearchResults({
  deals,
  events,
  profiles,
  query,
  venues,
}: {
  deals: VenuePublicDeal[];
  events: DiscoveryEvent[];
  profiles: ProfileSummary[];
  query: string;
  venues: VenueSummary[];
}): SmartSearchResult[] {
  const normalizedQuery = normalizeSearchText(query).replace(/^@/, '');
  const intent = detectSearchIntent(query);
  const candidates: ScoredSmartSearchResult[] = [];

  venues.forEach((venue) => {
    const matchScore = Math.max(
      textMatchScore(normalizedQuery, venue.name),
      textMatchScore(normalizedQuery, venue.address),
      textMatchScore(normalizedQuery, venue.category)
    );
    const exactMatch = normalizeSearchText(venue.name) === normalizedQuery;

    candidates.push({
      key: `venue:${venue.id}`,
      type: 'venue',
      item: venue,
      reason: resultReason(intent, 'venue', exactMatch),
      score: matchScore + intentBoost(intent, 'venue') + (venue.is_verified ? 8 : 0),
    });
  });

  deals.forEach((deal) => {
    const matchScore = Math.max(
      textMatchScore(normalizedQuery, deal.title),
      textMatchScore(normalizedQuery, deal.venue_name),
      textMatchScore(normalizedQuery, deal.category.replace(/_/g, ' ')),
      textMatchScore(normalizedQuery, deal.description)
    );
    const exactMatch = normalizeSearchText(deal.title) === normalizedQuery;

    candidates.push({
      key: `deal:${deal.id}`,
      type: 'deal',
      item: deal,
      reason: resultReason(intent, 'deal', exactMatch),
      score: matchScore + intentBoost(intent, 'deal'),
    });
  });

  events.forEach((event, index) => {
    const matchScore = Math.max(
      textMatchScore(normalizedQuery, event.title),
      textMatchScore(normalizedQuery, event.venue_name),
      textMatchScore(normalizedQuery, event.summary),
      ...event.tags.map((tag) => textMatchScore(normalizedQuery, tag)),
      ...event.attributes.map((attribute) => textMatchScore(normalizedQuery, attribute))
    );
    const exactMatch = normalizeSearchText(event.title) === normalizedQuery;

    candidates.push({
      key: `event:${event.id}`,
      type: 'event',
      item: event,
      duplicateGroupKey: getEventDuplicateGroupKey(event) ?? undefined,
      reason: resultReason(intent, 'event', exactMatch),
      score:
        matchScore +
        intentBoost(intent, 'event') +
        (event.is_boosted ? 6 : 0) +
        Math.max(0, 8 - index),
    });
  });

  profiles
    .filter((profile) => !profile.is_venue)
    .forEach((profile) => {
      const matchScore = Math.max(
        textMatchScore(normalizedQuery, profile.username),
        textMatchScore(normalizedQuery, profile.display_name),
        textMatchScore(normalizedQuery, profile.venue_name)
      );
      const exactMatch =
        normalizeSearchText(profile.username) === normalizedQuery ||
        normalizeSearchText(profile.display_name) === normalizedQuery;

      candidates.push({
        key: `profile:${profile.user_id}`,
        type: 'profile',
        item: profile,
        reason: resultReason(intent, 'profile', exactMatch),
        score: matchScore + intentBoost(intent, 'profile'),
      });
    });

  const limits: Record<SmartSearchResult['type'], number> = {
    venue: intent === 'balanced' || intent === 'scene' ? 3 : 2,
    deal: intent === 'deals' ? 4 : 2,
    event: intent === 'events' ? 4 : intent === 'scene' ? 2 : 3,
    profile: intent === 'people' ? 4 : 2,
  };
  const used: Record<SmartSearchResult['type'], number> = {
    venue: 0,
    deal: 0,
    event: 0,
    profile: 0,
  };

  return collapseDuplicateEvents(sortSmartResults(candidates, intent))
    .filter((result) => {
      if (result.score <= 0 || used[result.type] >= limits[result.type]) {
        return false;
      }

      used[result.type] += 1;
      return true;
    })
    .slice(0, 8);
}
